<script>
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import portalMixin from '../mixins/portalMixin';

export default {
  components: {
    Thumbnail,
  },
  mixins: [portalMixin],
  props: {
    portal: {
      type: Object,
      default: () => ({}),
    },
    active: {
      type: Boolean,
      default: false,
    },
    activePortalSlug: {
      type: String,
      default: '',
    },
    activeLocale: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedLocale: null,
    };
  },
  computed: {
    locales() {
      return this.portal?.config?.allowed_locales;
    },
    articlesCount() {
      const { allowed_locales: allowedLocales } = this.portal.config;
      return allowedLocales.reduce((acc, locale) => {
        return acc + locale.articles_count;
      }, 0);
    },
  },
  mounted() {
    this.selectedLocale = this.locale || this.portal?.meta?.default_locale;
  },
  methods: {
    onClick(event, code, portal) {
      event.preventDefault();
      this.$router.push({
        name: 'list_all_locale_articles',
        params: {
          portalSlug: portal.slug,
          locale: code,
        },
      });
      this.$emit('fetchPortal');
      this.$emit('openPortalPage');
    },
    isLocaleActive(code, slug) {
      const isPortalActive = this.portal.slug === slug;
      const isLocaleActive = this.activeLocale === code;
      return isPortalActive && isLocaleActive;
    },
    isLocaleDefault(code) {
      return this.portal?.meta?.default_locale === code;
    },
  },
};
</script>

<template>
  <div class="portal" :class="{ active }">
    <Thumbnail :username="portal.name" />
    <div class="actions-container">
      <header class="flex items-center justify-between mb-2.5">
        <div>
          <h3 class="text-sm mb-0.5 text-muted">
            {{ portal.name }}
          </h3>
          <p class="mb-0 text-xs text-muted">
            {{ articlesCount }}
            {{ $t('HELP_CENTER.PORTAL.ARTICLES_LABEL') }}
          </p>
        </div>
        <woot-label
          v-if="active"
          variant="smooth"
          size="small"
          color-scheme="success"
          :title="$t('HELP_CENTER.PORTAL.ACTIVE_BADGE')"
        />
      </header>
      <div class="portal-locales">
        <h5 class="text-base text-muted">
          {{ $t('HELP_CENTER.PORTAL.CHOOSE_LOCALE_LABEL') }}
        </h5>
        <ul>
          <li v-for="locale in locales" :key="locale.code">
            <woot-button
              :variant="`locale-item ${
                isLocaleActive(locale.code, activePortalSlug)
                  ? 'smooth'
                  : 'clear'
              }`"
              size="large"
              color-scheme="secondary"
              @click="event => onClick(event, locale.code, portal)"
            >
              <div class="flex items-center justify-between w-full">
                <div class="meta">
                  <h6 class="text-sm text-left mb-0.5">
                    <span class="text-muted">
                      {{ localeName(locale.code) }}
                    </span>
                    <span
                      v-if="isLocaleDefault(locale.code)"
                      class="text-sm text-subtle"
                    >
                      {{ `(${$t('HELP_CENTER.PORTAL.DEFAULT')})` }}
                    </span>
                  </h6>

                  <span
                    class="flex w-full text-sm leading-4 text-left text-muted"
                  >
                    {{ locale.articles_count }}
                    {{ $t('HELP_CENTER.PORTAL.ARTICLES_LABEL') }} -
                    {{ locale.code }}
                  </span>
                </div>
                <div v-if="isLocaleActive(locale.code, activePortalSlug)">
                  <fluent-icon icon="checkmark" class="locale__radio" />
                </div>
              </div>
            </woot-button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.portal {
  @apply bg-surface-raised rounded-md p-4 relative flex mb-4 border border-solid border-line-faint;

  &.active {
    @apply bg-surface-raised border border-solid border-woot-400 dark:border-woot-500;
  }

  .actions-container {
    @apply ml-2.5 rtl:ml-0 rtl:mr-2.5 flex-grow;

    .portal-locales {
      ul {
        @apply list-none p-0 m-0;
      }

      .locale__radio {
        @apply w-8 text-green-600 dark:text-green-600;
      }
    }
  }

  .locale-item {
    @apply flex items-start py-1 px-4 rounded-md w-full mb-2;

    p {
      @apply mb-0 text-left;
    }
  }
}
</style>
